import { type FC, useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { type SupportZendeskTicketModel } from "@doitintl/cmp-models";
import Box from "@mui/material/Box";

import { OPERATOR_AND } from "../../../Components/FilterTable/constants";
import { FilterTable } from "../../../Components/FilterTable/FilterTable";
import { FilterTableSkeleton } from "../../../Components/FilterTable/FilterTableSkeleton";
import { useCustomerId } from "../../../Components/hooks/useCustomerId";
import { useAuthContext } from "../../../Context/AuthContext";
import { Comparators, type DefaultFilterColumn, type DefaultFilterColumns } from "../../../types/FilterTable";
import { transformStatus } from "../../../utils/common";
import { useZendeskTicketsContext } from "../../Context/ZendeskTicketsContext";
import NoTickets from "../NoTickets";
import SupportTicketsRow from "./SupportTicketsRow";
import { supportTicketsColumnsData, supportTicketsColumnsHeaders, zendeskTicketColumns } from "./TicketsTableColumns";

type SupportTicketsTableProps = {
  zendeskTickets: Array<SupportZendeskTicketModel & { updatedAtMillis: number; zendeskIdNumeric: number }> | undefined;
  isDoitEmployee: boolean;
};

const SupportTicketsTable: FC<SupportTicketsTableProps> = ({ zendeskTickets, isDoitEmployee }) => {
  const { t } = useTranslation("services");
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const { assigneeUsers } = useZendeskTicketsContext();
  const customerId = useCustomerId();

  const rowComponent = useCallback(
    ({ row }) => (
      <SupportTicketsRow
        isDoitEmployee={isDoitEmployee}
        supportZendeskTicket={row}
        assigneeUsers={assigneeUsers ?? new Map()}
      />
    ),
    [assigneeUsers, isDoitEmployee]
  );

  const getFilterDefaultValue = useMemo((): DefaultFilterColumns<SupportZendeskTicketModel> => {
    const closedFilter: DefaultFilterColumn<SupportZendeskTicketModel> = {
      column: {
        label: zendeskTicketColumns(t)[0].label,
        path: "ticket.status",
      },
      comparator: Comparators.NOT_EQUAL_TO,
      value: "closed",
      label: transformStatus("closed", t),
    };

    const emailFilter: DefaultFilterColumn<SupportZendeskTicketModel> = {
      column: {
        label: zendeskTicketColumns(t)[2].label,
        path: "ticket.requester_email",
      },
      value: currentUser.email,
      label: currentUser.email,
    };

    return isDoitEmployee ? [closedFilter] : [closedFilter, OPERATOR_AND, emailFilter];
  }, [currentUser.email, isDoitEmployee, t]);

  return (
    <>
      {!zendeskTickets || !assigneeUsers ? (
        <Box sx={{ mt: 2 }}>
          <FilterTableSkeleton />
        </Box>
      ) : (
        <FilterTable<SupportZendeskTicketModel & { updatedAtMillis: number; zendeskIdNumeric: number }>
          toolbarProps={{ allowToEditColumns: false }}
          rowComponent={rowComponent}
          emptyTableComponent={<NoTickets />}
          defaultFilters={getFilterDefaultValue}
          headerColumns={supportTicketsColumnsHeaders(t)}
          filterColumns={supportTicketsColumnsData(t)}
          tableItems={zendeskTickets}
          persistenceKey={`support_filter_${customerId}`}
          defaultSortDirection="desc"
          defaultSortingColumnValue="updatedAtMillis"
          data-cy="supportTicketsTable"
        />
      )}
    </>
  );
};

export default SupportTicketsTable;
